<script setup lang="ts">
    import { useListen, useIgnore } from '~/composables/useEventBus';

    defineProps<{
        previewActive?: boolean;
    }>();

    const emit = defineEmits<{
        close: [];
        'close-preview': [];
    }>();

    // Sidebar is always open per design (no collapse). Context can still toggle.
    const isContextOpen = ref(true);
    const userToggledContext = ref(false);

    const toggleContext = () => {
        isContextOpen.value = !isContextOpen.value;
        userToggledContext.value = true;
    };

    useListen('ai:toggle-context', toggleContext);
    onBeforeUnmount(() => useIgnore('ai:toggle-context', toggleContext));

    const applyResponsiveDefaults = () => {
        if (!userToggledContext.value) {
            isContextOpen.value = window.innerWidth >= 1024;
        }
    };

    let resizeRafId: number | null = null;
    const throttledResize = () => {
        if (resizeRafId) return;
        resizeRafId = requestAnimationFrame(() => {
            applyResponsiveDefaults();
            resizeRafId = null;
        });
    };

    onMounted(() => {
        applyResponsiveDefaults();
        window.addEventListener('resize', throttledResize);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('resize', throttledResize);
        if (resizeRafId) cancelAnimationFrame(resizeRafId);
    });
</script>

<template>
    <div class="h-full w-full flex overflow-hidden">
        <!-- Sidebar — navy gradient, always open per design (250px) -->
        <aside class="flex-shrink-0 w-[250px] bg-sidebar-gradient text-white overflow-hidden">
            <slot name="sidebar" />
        </aside>

        <!-- Chat column -->
        <section
            class="flex-1 min-w-0 min-h-0 flex flex-col h-full overflow-hidden relative"
            style="background: linear-gradient(130deg, #f4f4ef 17.81%, #eef1f4 45.96%, #f0eef4 82.87%);"
        >
            <!-- Floating context toggle, visible when context is closed -->
            <button
                v-if="!isContextOpen"
                type="button"
                class="absolute top-4 right-5 z-[20] inline-flex items-center gap-[6px] h-8 px-3 rounded-full bg-white border border-[#E7E7E7] text-[12px] font-medium text-[#42484E] shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:border-[#FFC400] transition-colors cursor-pointer"
                @click="toggleContext"
            >
                <i class="fa-solid fa-folder-open text-[12px] text-[#E8B102]" />
                <span>Documents</span>
            </button>

            <slot name="chat" />
        </section>

        <!-- Context panel -->
        <aside
            class="border-l border-gray-200 flex-shrink-0 overflow-hidden bg-white transition-[width] duration-200"
            :class="[
                !isContextOpen && 'w-0',
                isContextOpen && !previewActive && 'w-[300px]',
                isContextOpen && previewActive && 'w-[50vw] max-w-[800px] min-w-[400px]',
            ]"
        >
            <div v-show="isContextOpen" class="w-full h-full">
                <slot name="context" />
            </div>
        </aside>
    </div>
</template>
